import type { RunLine } from "./runner";
import { mountHtmlPreview } from "./runner";

const KIND_CLASS: Record<RunLine["kind"], string> = {
  log: "text-foreground",
  info: "text-sky-400",
  warn: "text-amber-400",
  error: "text-red-400",
};

let cleanupPreview: (() => void) | null = null;

function appendConsoleLine(output: HTMLElement, line: RunLine) {
  const p = document.createElement("p");
  p.className = `whitespace-pre-wrap break-words ${KIND_CLASS[line.kind]}`;
  p.textContent = line.text;
  output.appendChild(p);
  output.scrollTop = output.scrollHeight;
}

function teardownPreview() {
  cleanupPreview?.();
  cleanupPreview = null;
}

export function initReviewPanel() {
  const backdrop = document.getElementById("review-backdrop")!;
  const panel = document.getElementById("review-panel")!;
  const closeBtn = document.getElementById("review-close")!;

  const close = () => {
    backdrop.classList.remove("open");
    panel.classList.remove("open");
    teardownPreview();
  };

  closeBtn.addEventListener("click", close);
  backdrop.addEventListener("click", close);
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && panel.classList.contains("open")) close();
  });
}

function openPanel() {
  document.getElementById("review-backdrop")!.classList.add("open");
  document.getElementById("review-panel")!.classList.add("open");
}

/** JS "Run": a snapshot of captured console output plus whether it timed out. */
export function showReviewPanel(lines: RunLine[], timedOut: boolean) {
  teardownPreview();

  const preview = document.getElementById("review-preview")!;
  const output = document.getElementById("review-console")!;
  preview.style.display = "none";
  output.style.flex = "1 1 auto";
  output.style.maxHeight = "none";
  output.innerHTML = "";

  if (timedOut) {
    const p = document.createElement("p");
    p.className = "mb-2 text-amber-400";
    p.textContent = "Stopped after 3s (possible infinite loop).";
    output.appendChild(p);
  }

  if (lines.length === 0 && !timedOut) {
    const p = document.createElement("p");
    p.className = "text-muted-foreground";
    p.textContent = "No output.";
    output.appendChild(p);
  }

  for (const line of lines) {
    appendConsoleLine(output, line);
  }

  openPanel();
}

/** HTML "Run": a live sandboxed preview, with console output streaming below it. */
export function showHtmlPreview(html: string) {
  teardownPreview();

  const preview = document.getElementById("review-preview")!;
  const output = document.getElementById("review-console")!;
  preview.style.display = "block";
  preview.style.flex = "1 1 auto";
  output.style.flex = "0 0 auto";
  output.style.maxHeight = "10rem";
  output.innerHTML = "";
  preview.innerHTML = "";

  cleanupPreview = mountHtmlPreview(preview, html, (line) => appendConsoleLine(output, line));

  openPanel();
}
